/**
 * The shared Reflecto HTTP client (desktop slice 3, R3-a/R3-b).
 *
 * Moved verbatim out of `apps/cli/src/transport/api.ts`, which was one of THREE
 * near-identical copies of this logic (extension 849 lines + web 886 + cli 204, each
 * confessing the duplication in a "Mirrors …" comment). `apps/desktop` consumes this from
 * its main process from its first line of code, so a fourth copy never gets written.
 *
 * The single behavioural rule for this module: **it throws `ApiError` and nothing else.** No
 * exit codes, no i18n keys, no host vocabulary — see `errors.ts`.
 *
 * ⚠️ This module is imported from Node (`apps/cli`, `packages/client-node`, and later the
 * Electron main process) as well as from browsers. That is why `ApiFetch` below is hand-
 * declared instead of `typeof globalThis.fetch`.
 */

import type {
  ApiError as ApiErrorBody,
  DeviceDiagnosticsStatusResponse,
  DeviceKeysResponse,
  ListDevicesResponse,
  PairInitRequest,
  PairInitResponse,
  PairStatusResponse,
  SendMessageRequest,
  SendMessageResponse,
  SurfaceKind,
  SyncResponse,
} from "@reflecto/core";
import type { PlatformFields } from "../preferences/platformFields";
import { ApiError } from "./errors";

/**
 * Hand-declared and deliberately minimal — NOT `typeof globalThis.fetch`.
 *
 * Inside `client-core` that alias resolves to lib.dom's fetch; inside `client-node` and the
 * Electron main process it resolves to undici's. Those declarations are structurally
 * different (distinct `Request`, `Response`, `Headers`), so a Node fetch is not assignable to
 * the DOM signature — the seam would fail to typecheck on the very runtime it exists to
 * serve. This shape is satisfied by both.
 */
export type ApiFetch = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string | Uint8Array;
    signal?: AbortSignal;
  }
) => Promise<{
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
  text(): Promise<string>;
}>;

export interface ApiClientDeps {
  /** Origin + version prefix, e.g. `https://sync.reflecto.dev` — paths below add `/v1/…`. */
  apiBase: string;
  /** Async-tolerant so a host can read a token from disk or a keychain per request. */
  getToken(): Promise<string | null> | string | null;
  /** Injectable so the desktop main process can supply a proxy-aware fetch, and so the
   *  library's own tests exercise the real request/response handling without a network. */
  fetch?: ApiFetch;
  /**
   * Sent on every request, before per-call headers.
   *
   * Exists because `X-Reflecto-Surface` is a client-level fact that was previously set
   * per-call inside `sendFeedback()` and nowhere else. Slice 2f then needed a SECOND value of
   * the same header on the same client (`cli-mcp`, identifying the local MCP server), which
   * is what makes it belong on the client rather than at a call site.
   */
  defaultHeaders?: Record<string, string>;
  /**
   * Which surface this client IS, sent as `X-Reflecto-Client-Kind` on pairing.
   *
   * ⚠️ REQUIRED, AND DELIBERATELY WITHOUT A DEFAULT. The server maps this header straight to
   * the device's stored `surface_kind` (`routes/pair.ts`), so it decides the glyph the phone
   * shows, what `GET /v1/devices` reports, and every surface-scoped query downstream. It was
   * previously hardcoded to `"cli"` inside `pairInit` — correct for the only caller that pairs
   * today, and a silent trap for the next one: `apps/desktop` would have registered itself as a
   * CLI device with nothing failing to say so.
   *
   * A default here would recreate that trap, so there is none. A shared client does not get to
   * assume an identity on its host's behalf; a new consumer must state it, and the typechecker
   * makes that a compile error rather than a wrong row in the database.
   *
   * Note this cannot be supplied via `defaultHeaders` — per-call headers win over defaults
   * (see `request`), so a hardcoded per-call value would override anything a host set there.
   */
  clientKind: Exclude<SurfaceKind, "phone">;
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

export interface ApiClient {
  pairInit(body: PairInitRequest): Promise<PairInitResponse>;
  pairStatus(deviceId: string): Promise<PairStatusResponse>;
  listDevices(): Promise<ListDevicesResponse>;
  getDeviceKeys(signal?: AbortSignal): Promise<DeviceKeysResponse>;
  sendMessage(body: SendMessageRequest, signal?: AbortSignal): Promise<SendMessageResponse>;
  sendClip(body: SendMessageRequest, signal?: AbortSignal): Promise<SendMessageResponse>;
  disconnectDevice(deviceId: string): Promise<void>;
  dismissMessage(msgId: string, to: string): Promise<void>;
  replyToMessage(
    msgId: string,
    body: { payload: string; to: string },
    signal?: AbortSignal
  ): Promise<void>;
  invokeAction(
    msgId: string,
    body: { payload: string; to: string },
    signal?: AbortSignal
  ): Promise<void>;
  sync(sinceId: number, signal?: AbortSignal): Promise<SyncResponse>;
  sendFeedback(
    body: { category: string; message: string; email?: string },
    headers: { appVersion: string; deviceId?: string; surface?: string }
  ): Promise<void>;
  /** Bulk dismiss. Chunked at the server's 200-id cap; see `BULK_DISMISS_CHUNK_SIZE`. */
  dismissMessages(msgIds: string[], to: string): Promise<void>;
  getDeviceDiagnostics(
    deviceId: string,
    signal?: AbortSignal
  ): Promise<DeviceDiagnosticsStatusResponse>;
  sendFileCtrlEnvelope(
    body: { payload: string; msgId: string; to: string },
    signal?: AbortSignal
  ): Promise<void>;
  renameDevice(deviceId: string, name: string, platform?: PlatformFields): Promise<void>;
}

/**
 * The server caps `POST /v1/messages/dismiss` at 200 unique ids per request. "Clear All" can
 * exceed that against the current card + thread UI caps, so the wrapper chunks rather than
 * either exceeding the contract or falling back to a per-card request storm (which tripped
 * infra rate-limiting on large backlogs and left cards un-dismissed). Both hosts had their own
 * copy of this constant and the loop around it; this is the single home.
 */
const BULK_DISMISS_CHUNK_SIZE = 200;

export function createApiClient(deps: ApiClientDeps): ApiClient {
  const doFetch: ApiFetch = deps.fetch ?? ((input, init) => fetch(input, init as RequestInit));

  /**
   * `Content-Type` only when there IS a body — it describes one, and a GET or a body-less
   * DELETE has none to describe.
   *
   * ⚠️ IT BUYS NO PREFLIGHT SAVINGS, contrary to what this comment said when it was written.
   * The original claim was that `Content-Type: application/json` disqualifies a GET from being
   * a CORS *simple request*, so `apps/web` paid an extra `OPTIONS`. That is false: the
   * CORS-safelisted request-header set is accept / accept-language / content-language /
   * content-type / range, and **`Authorization` is not in it**. Every one of these calls
   * carries a bearer token, so every one of them preflighted before this change and still
   * does. Corrected rather than deleted, because the wrong reason made this look like a
   * performance fix and would have been trusted from inside a shared module.
   *
   * The real reasons, both of which survive: the header is *meaningless* on a request with no
   * body, and dropping it closes the wire difference slice 11c recorded on two DELETEs —
   * which is what let slice 11e migrate `fetchDeviceDiagnostics` with its exact-header pin
   * INTACT. A pin correctly reporting a real wire change is not something to edit away.
   */
  function buildHeaders(token: string | null, hasBody: boolean): Record<string, string> {
    // Content-Type FIRST, so an explicit `defaultHeaders["Content-Type"]` from the host still
    // wins — the precedence this had before the change. Authorization last, as before.
    const h: Record<string, string> = {};
    if (hasBody) h["Content-Type"] = "application/json";
    Object.assign(h, deps.defaultHeaders);
    if (token) h.Authorization = `Bearer ${token}`;
    return h;
  }

  /**
   * Resolve the token WITHOUT awaiting when the host returns one synchronously.
   *
   * This looks like a micro-optimisation and is not: an unconditional `await` defers the
   * `doFetch` call by a microtask, so a caller that aborts immediately after invoking a method
   * — `const p = api.invokeAction(…, ctrl.signal); ctrl.abort();` — aborts BEFORE the request
   * is issued rather than after. Real `fetch` still rejects on an already-aborted signal, so
   * production behaviour survives either way, but the CLI's pre-extraction client built its
   * headers synchronously and `api.test.ts` pins the in-same-tick issue. Preserving it here
   * costs three lines; the alternative was editing that test to match the refactor, which is
   * the one move a migration is not allowed to make.
   */
  function isThenable(v: unknown): v is PromiseLike<string | null> {
    return typeof (v as PromiseLike<string | null> | null)?.then === "function";
  }

  async function request<T>(
    method: Method,
    path: string,
    body?: unknown,
    extraHeaders: Record<string, string> = {},
    signal?: AbortSignal,
    opts: { readBody?: boolean } = {}
  ): Promise<T> {
    // Inside a try/catch because `getToken` is a HOST callback and hosts have fallible token
    // sources — the CLI's `() => token ?? null` cannot fail, but the desktop app's will read a
    // `safeStorage`-sealed value off disk. Letting that throw raw would break this module's one
    // rule ("it throws ApiError and nothing else") for the exact consumer the seam exists for.
    // A try/catch adds no microtask, so the same-tick issue property described below survives.
    let token: string | null;
    try {
      const maybeToken = deps.getToken();
      token = isThenable(maybeToken) ? await maybeToken : maybeToken;
    } catch (err) {
      // `status: null` — the request never reached the server, which is the field's actual
      // meaning. A distinct code, because "your keychain is locked" and "the network is down"
      // have different fixes and a host must be able to tell them apart.
      throw new ApiError(
        "token_unavailable",
        `Could not read the auth token: ${err instanceof Error ? err.message : String(err)}`,
        null
      );
    }

    let res: Awaited<ReturnType<ApiFetch>>;
    try {
      res = await doFetch(`${deps.apiBase}${path}`, {
        method,
        // extraHeaders LAST so a per-call value wins over a default. `sendFeedback` relies on
        // this: `/v1/feedback` rejects an unknown `X-Reflecto-Surface` with a 400, so a client
        // whose default is `cli-mcp` must still send its own explicit surface there.
        // ONE predicate for both, deliberately: an earlier version tested `body !== undefined`
        // here and `body ?` below, so a defined-but-falsy body declared a Content-Type with no
        // body — the exact combination this change exists to prevent. No call site can pass
        // one today; the two lines still must not be able to disagree.
        headers: { ...buildHeaders(token, Boolean(body)), ...extraHeaders },
        body: body ? JSON.stringify(body) : undefined,
        signal,
      });
    } catch (err) {
      // status null — the request never reached the server, so this is retry-able rather than
      // re-authenticate-able. Hosts key their recovery off exactly this distinction.
      throw new ApiError(
        "network_error",
        `Failed to reach ${deps.apiBase}: ${err instanceof Error ? err.message : String(err)}`,
        null,
        null,
        err
      );
    }
    if (!res.ok) {
      let apiErr: ApiErrorBody | null = null;
      try {
        apiErr = (await res.json()) as ApiErrorBody;
      } catch {
        // not JSON
      }
      // The server's OWN error code survives whenever it sent a usable one, at every status.
      //
      // 403 and 404 used to be special-cased to a two-value allowlist, flattening every other
      // code to `forbidden`/`not_found`. That was lossy in a way no host could work around:
      // `apps/web`'s `disconnectDevice` treats a 404 carrying `device_not_found` as SUCCESS
      // ("already unpaired server-side") and could not distinguish it from a routing 404 once
      // the code had been flattened. Nothing is normalised away now.
      //
      // "Usable" is a real check, not a cast: `apiErr` came from `res.json()` and was ASSERTED
      // to be an `ApiErrorBody`, which proves nothing about an untrusted body. A `""` or a
      // non-string `error` would otherwise become the client's `code` verbatim — `??` only
      // falls back on null/undefined, so an empty string would have propagated as an empty
      // code and no host could branch on it.
      const serverCode =
        typeof apiErr?.error === "string" && apiErr.error !== "" ? apiErr.error : null;

      // Used ONLY when the server sent no usable code. These are the strings the previous
      // allowlist produced, kept so a bodyless 403/404 reads exactly as it did before.
      const FALLBACK_CODE: Record<number, string> = { 403: "forbidden", 404: "not_found" };
      const code =
        res.status === 401
          ? // ⚠️ THE ONE STATUS THAT IS DELIBERATELY NORMALISED, and the reason is the exact
            // opposite of what it looks like. `unauthorized` is not a code the server passes
            // through — **the server never sends it on any device-auth path**. `auth.ts`
            // answers 401 with `missing_token` (:128), `invalid_token_payload` (:165),
            // `device_not_found` (:241) or `invalid_token` (:270); the only `unauthorized` in
            // the tree is the dev-only `/debug` route.
            //
            // So this line SYNTHESISES the code that three CLI commands match on —
            // `commands/feedback.ts:100`, `commands/tail.ts:113`, `commands/status.ts:55` all
            // branch on `err.code === "unauthorized"` to show their re-pair guidance. Pass the
            // server's real 401 code through and all three silently stop firing: no error, no
            // failing test, just the wrong message for the most common auth failure there is.
            //
            // (An earlier version of this comment justified the pinning by saying `tail.ts`
            // branches on `token_expired`. That was wrong twice over: the server sends
            // `token_expired` at **403**, not 401, and `tail.ts` reads it from the SSEClient,
            // which never touches this module. Corrected rather than deleted, because the
            // wrong reason made this line look safe to remove.)
            "unauthorized"
          : (serverCode ?? FALLBACK_CODE[res.status] ?? `http_${res.status}`);
      // Same "usable, not merely present" rule as `serverCode` above, and for the same reason:
      // `apiErr` is a cast over an untrusted body, so `message` can be absent, empty, or not a
      // string at all.
      const serverMessage =
        typeof apiErr?.message === "string" && apiErr.message !== "" ? apiErr.message : null;
      throw new ApiError(
        code,
        serverMessage ?? `HTTP ${res.status}`,
        res.status,
        // Carried separately so a host can tell "the server explained itself" from "we made
        // something up" — `message` above can no longer answer that once it has fallen back.
        serverMessage
      );
    }
    // Nothing to hand back, so nothing to read — see `requestVoid`.
    if (opts.readBody === false) return undefined as T;
    if (res.status === 204) {
      // Body-returning endpoints should never produce 204. requestVoid routes through
      // request<unknown>, so the cast collapses to `undefined as unknown` in practice.
      return undefined as T;
    }
    // Tolerate an empty 2xx body (e.g. /v1/feedback 201 with none); non-empty still parses.
    const text = await res.text();
    return (text.trim() ? JSON.parse(text) : undefined) as T;
  }

  /**
   * Like `request`, but for endpoints whose body the caller does not want — and it does not
   * READ that body, rather than reading and discarding it.
   *
   * It used to route through `request<unknown>`, which meant every dismiss, reply, action and
   * clip send parsed a response nobody looked at. Two consequences, both real:
   *
   *   1. A malformed 2xx body threw `SyntaxError` out of a call whose result is `void`. The
   *      caller had asked for nothing and could still fail on the shape of nothing.
   *   2. It forced `ApiFetch` implementations to provide `text()` even for calls that produce
   *      no value. `apps/web`'s pre-migration client never read the body on these endpoints,
   *      so its test doubles never needed one — surfacing as `res.text is not a function` the
   *      moment those functions moved onto this client.
   *
   * Skipping the read is the behaviour `apps/web` already had, and the one the CLI's callers
   * cannot distinguish: `requestVoid`'s return value was `undefined` either way.
   */
  async function requestVoid(
    method: Method,
    path: string,
    body?: unknown,
    extraHeaders: Record<string, string> = {},
    signal?: AbortSignal
  ): Promise<void> {
    await request<unknown>(method, path, body, extraHeaders, signal, { readBody: false });
  }

  /** A 200 whose body is the wrong shape is a server/proxy fault, not an auth one — so it
   *  keeps the real status rather than becoming a transport failure. */
  function assertDeviceArray<T>(data: unknown, path: string, status: number): T {
    const devices = (data as { devices?: unknown } | null)?.devices;
    if (!data || typeof data !== "object" || !Array.isArray(devices)) {
      throw new ApiError("invalid_response", `Server returned unexpected ${path} shape.`, status);
    }
    return data as T;
  }

  return {
    pairInit(body) {
      return request("POST", "/v1/pair/init", body, { "X-Reflecto-Client-Kind": deps.clientKind });
    },

    pairStatus(deviceId) {
      return request("GET", `/v1/pair/status?deviceId=${encodeURIComponent(deviceId)}`);
    },

    async listDevices() {
      const data = await request<unknown>("GET", "/v1/devices");
      return assertDeviceArray<ListDevicesResponse>(data, "/v1/devices", 200);
    },

    async getDeviceKeys(signal) {
      const data = await request<unknown>("GET", "/v1/devices/keys", undefined, {}, signal);
      // No per-entry prevalidation: a bad key throws inside the caller's per-device try/catch
      // and lands in failed[], so one corrupt entry skips that device rather than aborting the
      // whole fan-out.
      return assertDeviceArray<DeviceKeysResponse>(data, "/v1/devices/keys", 200);
    },

    sendMessage(body, signal) {
      return request<SendMessageResponse>("POST", "/v1/messages", body, {}, signal);
    },

    /**
     * Distinct from `sendMessage` so the server delivers the inline `clipboard` FCM command to
     * Android instead of the content-free notify nudge a phone never acts on.
     */
    sendClip(body, signal) {
      return request<SendMessageResponse>("POST", "/v1/clipboard", body, {}, signal);
    },

    async dismissMessages(msgIds, to) {
      for (let start = 0; start < msgIds.length; start += BULK_DISMISS_CHUNK_SIZE) {
        const ids = msgIds.slice(start, start + BULK_DISMISS_CHUNK_SIZE);
        await requestVoid("POST", "/v1/messages/dismiss", { ids, to });
      }
    },

    getDeviceDiagnostics(deviceId, signal) {
      // No response-shape validation, deliberately: both hosts returned the parsed body cast
      // to the response type, and `session.ts` / `pushCapability.ts` read one optional field
      // off it. Adding a validator here would be a behaviour change dressed as a move.
      return request<DeviceDiagnosticsStatusResponse>(
        "GET",
        `/v1/diagnostics/devices/${encodeURIComponent(deviceId)}`,
        undefined,
        {},
        signal
      );
    },

    /**
     * Distinct from `sendMessage` so the server delivers an INLINE `file_ctrl` FCM data
     * message to Android partners — Android has no `/sync` consumer for ctrls, so they would
     * never arrive over that leg — while extension/web partners still get enqueue+SSE.
     */
    sendFileCtrlEnvelope(body, signal) {
      return requestVoid("POST", "/v1/file-ctrl", body, {}, signal);
    },

    renameDevice(deviceId, name, platform = {}) {
      // Spread, so an unrecognised browser/OS sends NOTHING for that field rather than a
      // sentinel. The server's UPDATE uses COALESCE, so an omitted field preserves whatever
      // an earlier build reported.
      return requestVoid("PATCH", `/v1/devices/${encodeURIComponent(deviceId)}`, {
        name,
        ...platform,
      });
    },

    /** Multi-device V1: remove only this device, preserving siblings. */
    disconnectDevice(deviceId) {
      return requestVoid("DELETE", `/v1/devices/${encodeURIComponent(deviceId)}`);
    },

    dismissMessage(msgId, to) {
      return requestVoid(
        "DELETE",
        `/v1/messages/${encodeURIComponent(msgId)}?to=${encodeURIComponent(to)}`
      );
    },

    replyToMessage(msgId, body, signal) {
      return requestVoid(
        "POST",
        `/v1/messages/${encodeURIComponent(msgId)}/reply`,
        body,
        {},
        signal
      );
    },

    invokeAction(msgId, body, signal) {
      return requestVoid(
        "POST",
        `/v1/messages/${encodeURIComponent(msgId)}/action`,
        body,
        {},
        signal
      );
    },

    sync(sinceId, signal) {
      return request("GET", `/v1/sync?since_id=${sinceId}`, undefined, {}, signal);
    },

    /** Unauthenticated feedback intake — surface + app-version headers; deviceId optional. */
    sendFeedback(body, hdrs) {
      const extra: Record<string, string> = {
        "X-Reflecto-Surface": hdrs.surface ?? "cli",
        "X-Reflecto-App-Version": hdrs.appVersion,
      };
      if (hdrs.deviceId) extra["X-Reflecto-Device-Id"] = hdrs.deviceId;
      return requestVoid("POST", "/v1/feedback", body, extra);
    },
  };
}
